<template>
  <div class="quote-quantity-wrapper">
    <div class="quote-quantity-box">
      <input
        ref="inputRef"
        type="number"
        :min="minimum_quantity"
        :max="hasNoMax ? undefined : order_max_quantity"
        :step="increment_quantity"
        :value="modelValue"
        class="quote-quantity-input"
        @input="onInput"
        @change="onChange"
      />
      <!--
        Unit label tooltip (only when CSS ellipsis is active — see unitOverflows):

        • prefersHoverTooltip true (≥992px AND (hover: hover)):
          vue3-popper hover mode; :show stays null (not manual).

        • prefersHoverTooltip false (narrow layout and/or coarse pointer):
          long-press on .quote-quantity-unit opens tooltip; :show binds unitTooltipOpen.
          Tap outside dismisses (manual show disables popper click-away).

        ToolTip props: disabled=!unitOverflows | hover | show | interactive
        See useTooltipHoverMode() in composables/deviceType.js.
      -->
      <ToolTip
        v-if="displayUnit"
        :content="displayUnit"
        :disabled="!unitOverflows"
        :hover="prefersHoverTooltip"
        :show="prefersHoverTooltip ? null : unitTooltipOpen"
        :interactive="!prefersHoverTooltip"
        className="quote-quantity-unit-tooltip mw-100"
        placement="bottom"
      >
        <div
          ref="unitRef"
          class="quote-quantity-unit"
          @touchstart.passive="onUnitTouchStart"
          @touchend="onUnitTouchEnd"
          @touchcancel="onUnitTouchEnd"
          @touchmove.passive="onUnitTouchMove"
          @contextmenu="onUnitContextMenu"
        >
          {{ displayUnit }}
        </div>
      </ToolTip>
      <div class="quote-quantity-controls">
        <button
          type="button"
          class="quote-quantity-control-button"
          aria-label="Increase quantity"
          :disabled="!canIncrement"
          @click="stepQuantity('up')"
        >
          <svg class="icon-sprite" aria-hidden="true" width="10" height="10">
            <use href="#icon-increment" />
          </svg>
        </button>
        <button
          type="button"
          class="quote-quantity-control-button"
          aria-label="Decrease quantity"
          :disabled="!canDecrement"
          @click="stepQuantity('down')"
        >
          <svg class="icon-sprite" aria-hidden="true" width="10" height="10">
            <use href="#icon-decrement" />
          </svg>
        </button>
      </div>
    </div>
  </div>
  <div class="qty-info">
    <ToolTipIcon :content="minimum_message" className="minimum-quantity-message" iconHref="#icon-info" v-if="minimum_quantity > 1" />
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import ToolTipIcon from './ToolTipIcon.vue';
import ToolTip from './ToolTip.vue';
import { useTooltipHoverMode } from '@/composables/deviceType';

/** Duration before long-press opens the unit tooltip on touch/coarse-pointer layouts. */
const LONG_PRESS_MS = 450;

/**
 * Responsive tooltip trigger for the unit label:
 * - Desktop + fine pointer → hover (prefersHoverTooltip === true)
 * - Otherwise → long-press + manual :show on ToolTip
 *
 * Not the same as isTouchDevice(): a narrow desktop window uses long-press;
 * a large touch tablet without (hover: hover) uses long-press too.
 */
const { prefersHoverTooltip } = useTooltipHoverMode();

const props = defineProps({
  modelValue: {
    type: Number,
    default: 1,
  },
  minimum_quantity: {
    type: Number,
    default: 1,
  },
  increment_quantity: {
    type: Number,
    default: 1,
  },
  order_max_quantity: {
    type: Number,
    default: -1,
  },
  unit_singular: {
    type: String,
    default: '',
  },
  unit_plural: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const inputRef = ref(null);
const unitRef = ref(null);
const min_message = ref('');
/** True when .quote-quantity-unit text is wider than its box (ellipsis shown). */
const unitOverflows = ref(false);
/** Manual open state for ToolTip when prefersHoverTooltip is false. */
const unitTooltipOpen = ref(false);

let longPressTimer = null;

/** Touch / long-press path (ignored when hover mode or label is not truncated). */

const closeUnitTooltip = () => {
  unitTooltipOpen.value = false;
};

const clearLongPress = () => {
  if (longPressTimer) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
};

const onUnitTouchStart = () => {
  if (prefersHoverTooltip.value || !unitOverflows.value) return;
  clearLongPress();
  longPressTimer = setTimeout(() => {
    unitTooltipOpen.value = true;
  }, LONG_PRESS_MS);
};

const onUnitTouchEnd = () => {
  clearLongPress();
};

const onUnitTouchMove = () => {
  clearLongPress();
};

const onUnitContextMenu = (event) => {
  // Avoid native callout over our long-press tooltip on touch layouts.
  if (!prefersHoverTooltip.value && unitOverflows.value) {
    event.preventDefault();
  }
};

/** vue3-popper click-away does not run in manual :show mode; dismiss on outside tap. */
const onDocumentDismissUnitTooltip = (event) => {
  if (!unitTooltipOpen.value) return;
  const el = unitRef.value;
  if (el && !el.contains(event.target)) {
    closeUnitTooltip();
  }
};

/** Detect truncated unit text so tooltip is not shown for short labels (e.g. "stem"). */
const checkUnitOverflow = () => {
  const el = unitRef.value;
  if (!el) {
    unitOverflows.value = false;
    return;
  }
  if (el.scrollWidth > el.clientWidth) {
    unitOverflows.value = true;
    return;
  }
  // Ellipsis can make scrollWidth === clientWidth in some browsers; measure text width.
  const range = document.createRange();
  range.selectNodeContents(el);
  const textWidth = range.getBoundingClientRect().width;
  const boxWidth = el.getBoundingClientRect().width;
  unitOverflows.value = textWidth > boxWidth + 0.5;
};

const scheduleUnitOverflowCheck = () => {
  nextTick(checkUnitOverflow);
};

const hasNoMax = computed(() => Number(props.order_max_quantity) < 0);

const displayUnit = computed(() => {
  const qty = Number(props.modelValue) || 0;
  const singular = String(props.unit_singular || '').toLowerCase();
  const plural = String(props.unit_plural || props.unit_singular || '').toLowerCase();
  return qty === 1 ? singular : plural;
});

const minimum_message = computed(() => {
  const unit = props.minimum_quantity > 1 ? props.unit_plural : props.unit_singular;
  const suffix = unit ? ` ${unit}` : '';
  return `${min_message.value} ${props.minimum_quantity}${suffix}`.trim();
});

const quantityFloor = computed(() => {
  const min = Number(props.minimum_quantity) || 1;
  const inc = Number(props.increment_quantity) || 1;
  return Math.max(min, inc);
});

const canIncrement = computed(() => {
  if (hasNoMax.value) return true;
  const current = Number(props.modelValue) || 0;
  const inc = Number(props.increment_quantity) || 1;
  return (current + inc) <= Number(props.order_max_quantity);
});

const canDecrement = computed(() => {
  const current = Number(props.modelValue) || 0;
  const inc = Number(props.increment_quantity) || 1;
  return (current - inc) >= quantityFloor.value;
});

const commitQuantity = (value, { emitChange = false } = {}) => {
  const nextValue = parseInt(value, 10) || 0;
  emit('update:modelValue', nextValue);
  if (emitChange) emit('change', nextValue);
};

const onInput = (event) => {
  commitQuantity(event.target.value);
};

const onChange = (event) => {
  commitQuantity(event.target.value, { emitChange: true });
};

const stepQuantity = (direction) => {
  const current = Number(props.modelValue) || 0;
  const inc = Number(props.increment_quantity) || 1;
  if (direction === 'up') {
    if (!canIncrement.value) return;
    commitQuantity(current + inc, { emitChange: true });
    return;
  }
  if (!canDecrement.value) return;
  commitQuantity(current - inc, { emitChange: true });
};

watch(displayUnit, scheduleUnitOverflowCheck);

watch(
  () => [props.unit_singular, props.unit_plural],
  scheduleUnitOverflowCheck,
);

watch(prefersHoverTooltip, (useHover) => {
  // Leaving touch/long-press mode — close any manually opened tooltip.
  if (useHover) {
    closeUnitTooltip();
  }
});

watch(unitTooltipOpen, (isOpen) => {
  // Outside dismiss listeners while manual tooltip is open (see onDocumentDismissUnitTooltip).
  if (isOpen) {
    document.addEventListener('touchstart', onDocumentDismissUnitTooltip, { passive: true, capture: true });
    document.addEventListener('mousedown', onDocumentDismissUnitTooltip, { capture: true });
  } else {
    document.removeEventListener('touchstart', onDocumentDismissUnitTooltip, { capture: true });
    document.removeEventListener('mousedown', onDocumentDismissUnitTooltip, { capture: true });
  }
});

onMounted(() => {
  const siteSettings = JSON.parse(localStorage.getItem('site_settings') || '{}');
  min_message.value = siteSettings.minimum_quantity_message || '';
  scheduleUnitOverflowCheck();
});

onUnmounted(() => {
  clearLongPress();
  document.removeEventListener('touchstart', onDocumentDismissUnitTooltip, { capture: true });
  document.removeEventListener('mousedown', onDocumentDismissUnitTooltip, { capture: true });
});
</script>

<style scoped>
.quote-quantity-wrapper {
  display: inline-flex;
  align-items: center;
  gap: var(--gap-5);
}

.quote-quantity-box {
  padding: var(--padding-1) var(--padding-3);
  padding-right: var(--padding-19);
  text-align: center;
  border: 2px solid var(--color-gainsboro);
  border-radius: var(--br-10);
  position: relative;
  width: 72px;
  background: var(--body-bg);
  overflow: visible;
  display: flex;
  flex-direction: column;
}

.quote-quantity-box .quote-quantity-controls {
  position: absolute;
  right: 3px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-direction: column;
  gap: 0;
  width: 16px;
  height: 100%;
}

.quote-quantity-box .quote-quantity-control-button {
  outline: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  background-color: #efefef;
  border-radius: 2px;
  font-size: 6px;
  line-height: 1;
}

.quote-quantity-box .quote-quantity-control-button:hover:not(:disabled) {
  color: var(--color-gray);
  opacity: 0.7;
}

.quote-quantity-box .quote-quantity-control-button:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.quote-quantity-input {
  appearance: textfield !important;
  -moz-appearance: textfield !important;
  border: 0;
  box-shadow: none;
  text-align: center;
  background: transparent;
  padding: 0;
  border: none;
  box-shadow: none;
  width: 100%;
  color: var(--body-color);
  font-size: var(--font-size-16);
  font-style: normal;
  font-weight: 400;
  display: block;
  line-height: 20px;
  min-height: 0;
}

.quote-quantity-input::-webkit-outer-spin-button,
.quote-quantity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.quote-quantity-unit {
  display: block;
  overflow: hidden;
  color: var(--body-color);
  text-align: center;
  text-overflow: ellipsis;
  font-size: var(--font-size-12);
  font-style: normal;
  font-weight: 400;
  line-height: 1;
  -webkit-box-orient: vertical;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -moz-line-clamp: 1;
  -ms-line-clamp: 1;
  -o-line-clamp: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.quote-quantity-box :deep(.quote-quantity-unit-tooltip) {
  display: block;
  width: 100%;
  min-width: 0;
  margin:0 !important;
  border:0 !important;
}

.quote-quantity-box :deep(.quote-quantity-unit-tooltip .popper) {
  z-index: 10000;
}

.qty-info {
  width: 14px;
  height: 14px;
  font-size: 14px;
}

.qty-info :deep(.inline-block) {
  border: 0 !important;
}

</style>
